import { DialogTextBody } from '@common/Dialog/DialogTextBody';
import { ORCHESTRATION_PROVIDER } from '@common/Fabric/types/orchestration';
import { useUnifiedFabricContext } from '@common/Fabric/UnifiedFabrics/context';
import { useConsolidatedProviderOptions } from '@common/Fabric/useProviderOptions';
import { Private_Routes } from '@common/url';
import { useUserWorkspace } from '@common/VerticalApps/hooks';
import { VAppFabricProviderTypeWithoutProphecyManaged } from '@common/VerticalApps/types';
import { TestConnectionProvider, useTestConnection } from '@pages/Fabric/common/TestConnectionContext';
import useInitialFabricData from '@pages/Fabric/hooks/useInitialFabricData';
import { FabricSetupState } from '@pages/UserSettings/contexts/constants';
import { EnvironmentIcon } from '@prophecy/icons';
import { UpdateTeamVAppDefaultsQuery } from '@prophecy/queries/pages/Team/queries';
import { BaseColor, theme } from '@prophecy/theme';
import { Button, Dialog, isFormDirty, Select, Spinner, Stack, Text, Tooltip } from '@prophecy/ui-v3';
import { useValidForm } from '@prophecy/ui-v3/Form/useValidForm';
import { EntityIcon } from '@prophecy/ui-v3/Misc/EntityIcon';
import { useGraphQlMutation } from 'frontend/core/src/data/util';
import { noop } from 'lodash-es';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { PageHeader } from '../../../../common/Header/PageHeader';
import { ProviderFormProvider } from '../../../Fabric/common/ProviderFormContext';
import useCreateFabric from '../../../Fabric/hooks/useCreateFabric';
import useUpdateFabric from '../../../Fabric/hooks/useUpdateFabric';
import { FABRIC_TABS, FormContexts } from '../../../Fabric/types';
import { useWorkspaceFabricContext } from '../../contexts/WorkspaceFabricContext/context';
import { BigQuerySetupForm } from './bigquery/BigQuerySetupForm';
import { DatabricksSetupForm } from './databricks/DatabricksSetupForm';
import { KnowledgeGraphIndexerForm } from './databricks/KnowledgeGraphIndexerForm';
import { PROVIDER_META } from './providerMeta';
import { ProviderHeader } from './ReadOnlyFabricView';
import { SnowflakeSetupForm } from './snowflake/SnowflakeSetupForm';

// ─── Layout styled components ─────────────────────────────────────────────────

const SetupRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${theme.colors.gray25};
`;

const StyledPageHeader = styled(PageHeader)`
  background: ${theme.colors.white};
  border-bottom: 1px solid ${theme.colors.gray200};
`;

const ScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: ${theme.spaces.x40} ${theme.spaces.x32};
`;

const ContentCard = styled(Stack)`
  background: ${theme.colors.white};
  border-radius: ${theme.radius.x12};
  width: 760px;
  max-width: 100%;
  padding: ${theme.spaces.x32};
  flex-direction: column;
  border: 1px solid ${theme.borderColor.tertiary};
  gap: ${theme.spaces.x24};
  min-height: 300px;
`;

const PageFooter = styled(Stack)`
  background: ${theme.colors.white};
  border-top: 1px solid ${theme.colors.gray200};
  padding: ${theme.spaces.x12};
  flex-shrink: 0;
`;

const StyledSelectOption = styled(Stack)`
  svg {
    width: ${theme.sizes.x20};
    height: ${theme.sizes.x20};
  }
`;

// Each form owns its own editable fields, read-only summary, and auth card —
// SetupEnvironment only dispatches to the right one for the selected warehouse.
const FORM_BY_WAREHOUSE: Partial<
  Record<VAppFabricProviderTypeWithoutProphecyManaged, (props: { isReadonly: boolean }) => JSX.Element | null>
> = {
  [ORCHESTRATION_PROVIDER.Databricks]: DatabricksSetupForm,
  [ORCHESTRATION_PROVIDER.Snowflake]: SnowflakeSetupForm,
  [ORCHESTRATION_PROVIDER.BigQuery]: BigQuerySetupForm
};

const SetupEnvironmentInner = ({
  provider,
  fabricId,
  formContexts,
  isReadonly
}: {
  provider: VAppFabricProviderTypeWithoutProphecyManaged;
  fabricId: string | undefined;
  formContexts: FormContexts;
  isReadonly: boolean;
}) => {
  const FormComponent = FORM_BY_WAREHOUSE[provider];
  const teamId = formContexts[FABRIC_TABS.basic].getValues('ownerUID') ?? '';
  const providerFormContext = formContexts[FABRIC_TABS.providers];

  const { watch } = providerFormContext;

  const { orchestrationProviderInfo, setUnifiedFabricDataplaneURL, onProviderChange } = useInitialFabricData(
    providerFormContext,
    teamId
  );

  const dataPlaneUrl = watch('unifiedFabricData.dataplaneURL');
  useEffect(() => {
    if (!orchestrationProviderInfo.dataplaneUrls.length || dataPlaneUrl) {
      return;
    }
    setUnifiedFabricDataplaneURL(orchestrationProviderInfo);
  }, [orchestrationProviderInfo, setUnifiedFabricDataplaneURL, dataPlaneUrl]);

  const handleProviderChange = async (value: string) => {
    onProviderChange({
      providerV2: value
    });
  };

  const providerOptions = useConsolidatedProviderOptions();
  const providerMeta = PROVIDER_META[provider];
  const fabricAuthState = useUnifiedFabricContext((state) => state.fabricAuthState);

  if (!FormComponent) {
    return null;
  }

  return (
    <>
      {fabricId ? (
        providerMeta && (
          <ProviderHeader
            providerName={providerMeta.displayName}
            providerIcon={<providerMeta.icon />}
            providerColor={providerMeta.color}
          />
        )
      ) : (
        <Select
          label='Data Warehouse'
          required
          value={provider}
          onChange={handleProviderChange}
          optionLabelProp='label'
          data-test-id='setup-env-warehouse-select'>
          {providerOptions.map(({ label, value, icon, disabledMessage }) => {
            const content = (
              <StyledSelectOption direction='horizontal' alignY='center' gap={theme.spaces.x6}>
                {icon}
                <span>{label}</span>
              </StyledSelectOption>
            );
            return (
              <Select.Option key={value} value={value} label={content} disabled={!!disabledMessage}>
                <Tooltip title={disabledMessage}>{content}</Tooltip>
              </Select.Option>
            );
          })}
        </Select>
      )}
      <ProviderFormProvider
        fabricId={fabricId}
        isCreateMode={!fabricId}
        isOrchestrationFabric={false}
        teamId={formContexts[FABRIC_TABS.basic].getValues('ownerUID') ?? ''}
        formContext={formContexts[FABRIC_TABS.providers]}
        orchestrationProviderInfo={orchestrationProviderInfo}
        updateFabricErrors={noop}
        updateFabricProviderTypeV2={noop}
        selectedProviderOption={undefined}
        fabricAuthState={fabricAuthState}>
        <FormComponent isReadonly={isReadonly} />
        {fabricId && <KnowledgeGraphIndexerForm provider={provider} isReadonly={isReadonly} />}
      </ProviderFormProvider>
    </>
  );
};

// ─── SetupEnvironment ──────────────────────────────────────────────────────────

const SetupEnvironmentComponent = () => {
  const navigate = useNavigate();
  const { isAdmin, formContexts, fabricId, fabricSetupState, workspaceId, isFabricLoading } =
    useWorkspaceFabricContext();

  const { currentFabric } = useUserWorkspace();

  const connectionsUrl = Private_Routes.Settings.tab.getUrl({ tab: 'connections' });

  const title = fabricSetupState === FabricSetupState.Configured ? 'Re-Configure Environment' : 'Set Up Environment';
  const isReadonly = !isAdmin;

  const { testConnection, isLoading: testConnectionLoading, shouldTestConnection } = useTestConnection();

  // Save logic lives here so the footer can access isBusy.
  const { createNewFabric, isLoading: isCreatingFabric } = useCreateFabric(formContexts, true);
  const { updateExistingFabric, isLoading: isUpdating } = useUpdateFabric(fabricId || '', formContexts, true);

  const { mutateAsync: updateTeamVerticalAppDefaults, isLoading: isUpdatingTeamVerticalAppDefaults } =
    useGraphQlMutation(UpdateTeamVAppDefaultsQuery);
  const isBusy = isCreatingFabric || isUpdating || isUpdatingTeamVerticalAppDefaults || testConnectionLoading;

  const { isValid: isProviderFormValid } = useValidForm(formContexts[FABRIC_TABS.providers]);
  const isProviderFormDirty = isFormDirty(formContexts[FABRIC_TABS.providers]);

  const handleDone = async () => {
    try {
      // Switching to a different provider is not supported in current version anyway
      const isUpdatingExisting =
        fabricSetupState === FabricSetupState.Configured &&
        selectedProvider === currentFabric?.fabricProviderInfo.provider;

      if (!isUpdatingExisting) {
        const providerName = PROVIDER_META[selectedProvider]?.displayName ?? 'this environment';

        const confirmed = await Dialog.info({
          'data-test-id': 'setup-env-default-confirm-dialog',
          children: (
            <DialogTextBody
              title={`${providerName} will be your default environment`}
              subtitle={
                <>
                  New projects will run on {providerName}. Existing projects on Prophecy&apos;s managed warehouse
                  won&apos;t change and will keep working as usual.
                </>
              }
            />
          ),
          closeButton: { children: 'Cancel' },
          okButton: { children: 'Okay' },
          style: { width: '520px' }
        });

        if (confirmed !== Dialog.ResponseAction.okButton) {
          return;
        }
      }

      if (shouldTestConnection) {
        const result = await testConnection();
        if (!result) {
          return;
        }
      }

      if (isUpdatingExisting) {
        await updateExistingFabric();
      } else {
        const fabricId = await createNewFabric();
        await updateTeamVerticalAppDefaults({
          teamUID: workspaceId!,
          fabricId,
          fabricType: selectedProvider
        });
      }
      navigate(connectionsUrl);
    } catch {
      // error already surfaced by useRestMutation toast; stay on page
    }
  };

  const selectedProvider = formContexts[FABRIC_TABS.providers].watch(
    'providerV2'
  ) as VAppFabricProviderTypeWithoutProphecyManaged;

  return (
    <SetupRoot>
      <StyledPageHeader
        crumbs={[
          {
            label: title,
            icon: (
              <EntityIcon
                size='l'
                variant='boxLight'
                icon={<EnvironmentIcon type='extended' />}
                color={BaseColor.greenLight}
              />
            )
          }
        ]}
        showCategory={false}
        rightContent={
          <Button
            variant='plain'
            size='s'
            aria-label='Close'
            data-test-id='setup-env-close'
            onClick={() => navigate(connectionsUrl)}>
            <Text level='md' tone={theme.colors.gray500}>
              ✕
            </Text>
          </Button>
        }
      />

      {isFabricLoading ? (
        <Stack grow='1' height='100%' align='center' alignY='center'>
          <Spinner spinning={isFabricLoading} />
        </Stack>
      ) : (
        <ScrollContent>
          <ContentCard>
            <SetupEnvironmentInner
              provider={selectedProvider}
              fabricId={fabricId}
              formContexts={formContexts}
              isReadonly={isReadonly}
            />
          </ContentCard>
        </ScrollContent>
      )}

      <PageFooter direction='horizontal' align='end' gap={theme.spaces.x12}>
        <Button
          variant='secondaryGray'
          size='m'
          onClick={() => navigate(connectionsUrl)}
          data-test-id='setup-env-cancel'>
          Cancel
        </Button>
        {!isReadonly && (
          <Button
            variant='primary'
            size='m'
            loading={isBusy}
            disabled={!isProviderFormValid || !isProviderFormDirty}
            onClick={handleDone}
            data-test-id='setup-env-done'>
            Done
          </Button>
        )}
      </PageFooter>
    </SetupRoot>
  );
};

export const SetupEnvironment = () => (
  <TestConnectionProvider>
    <SetupEnvironmentComponent />
  </TestConnectionProvider>
);
